/**
 * V3 RAG Service
 * Enhanced RAG with Gemini table extraction, LLM metadata, answer synthesis
 */

import { API_ENDPOINTS, API_KEY } from '../config/api';

export interface V3QueryResponse {
  answer: string;
  query_type: string | null;
  confidence: number | null;
  language?: string;
  sources: Array<{
    id: string;
    doc_marker?: string;
    content: string;
    similarity: number;
    document_id?: string;
    block_type?: string;
    metadata?: {
      block_id?: string;
      ref_id?: number | string;  // V3 block reference ID for highlighting
      page?: number;  // Page number for V3 block highlighting
      title?: string;
      analysts?: string[];
      report_date?: string;
    };
  }>;
}

export interface V3SearchResponse {
  query: string;
  metadata_answer: any;
  results: Array<{
    id: string;
    content: string;
    similarity: number;
    doc_id: string;
  }>;
}

export interface V3Document {
  id: string;
  filename: string;
  title: string | null;
  source: string | null;
  report_date: string | null;
  doc_type: string | null;
  analyst_names: string[] | null;
  company_tickers: string[] | null;
}

export interface V3Stats {
  documents: number;
  chunks: number;
  analysts: number;
  companies: number;
  sectors: number;
}

export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

// Company list types
export interface CompanyListItem {
  id: string;
  name: string;
  ticker: string;
  sector: string | null;
  sub_sector: string | null;
  gics_sector: string | null;
  market_cap: string | null;
  coverage_status: string | null;
  rating: string | null;
  price_target: number | null;
  rating_change: string | null;
  last_report_date: string | null;
  has_rating?: boolean;
  extraction_confidence?: number | null;
  current_price?: number | null;
  price_date?: string | null;
  upside?: number | null;
}

/**
 * Get all companies with latest ratings
 */
export const getCompanies = async (): Promise<CompanyListItem[]> => {
  const response = await fetch(`${API_ENDPOINTS.v3Documents.replace('/documents', '/companies')}`, {
    headers: {
      'X-API-Key': API_KEY,
    },
  });

  if (!response.ok) {
    throw new Error(`Companies list failed: ${response.status}`);
  }

  const data = await response.json();
  return data.companies;
};

// Analyst list types
export interface AnalystListItem {
  id: string;
  name: string;
  firm: string | null;
  sector_focus: string | null;
  document_count: number | null;
  last_document_date: string | null;
  ii_ranking: number | null;
}

export interface AnalystProfile {
  analyst: {
    id: string;
    name: string;
    firm: string | null;
    email: string | null;
    bio: string | null;
    expertise: string[] | null;
    sector_focus: string | null;
    ii_ranking: number | null;
    years_at_firm: number | null;
    document_count: number | null;
    last_document_date: string | null;
  };
  companies: Array<{
    id: string;
    ticker: string;
    name: string | null;
    sector: string | null;
    sub_sector: string | null;
    gics_sector: string | null;
  }>;
  documents: Array<{
    id: string;
    title: string | null;
    report_date: string | null;
    report_type: string | null;
    company_tickers: string[] | null;
    sector: string | null;
  }>;
}

/**
 * Get all analysts
 */
export const getAnalysts = async (): Promise<AnalystListItem[]> => {
  const response = await fetch(`${API_ENDPOINTS.v3Documents.replace('/documents', '/analysts')}`, {
    headers: {
      'X-API-Key': API_KEY,
    },
  });

  if (!response.ok) {
    throw new Error(`Analysts list failed: ${response.status}`);
  }

  const data = await response.json();
  return data.analysts;
};

/**
 * Get analyst profile by ID
 */
export const getAnalystProfile = async (id: string): Promise<AnalystProfile> => {
  const response = await fetch(`${API_ENDPOINTS.v3Documents.replace('/documents', '/analyst')}/${id}`, {
    headers: {
      'X-API-Key': API_KEY,
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Analyst profile failed: ${response.status}`);
  }

  return response.json();
};

// Company Profile types
export interface CompanyProfile {
  company: {
    id: string;
    name: string;
    ticker: string;
    sector: string | null;
    sub_sector: string | null;
    market_cap: number | null;
    coverage_status: string | null;
  };
  current_rating: {
    rating: string | null;
    price_target: number | null;
    rating_change: string | null;
    prev_rating: string | null;
    prev_target: number | null;
    report_date: string | null;
    valuation_methodology: string | null;
  } | null;
  rating_history: Array<{
    report_date: string | null;
    rating: string | null;
    price_target: number | null;
    rating_change: string | null;
    prev_rating: string | null;
    prev_target: number | null;
  }>;
  latest_price: {
    close_price: number;
    market_cap: number | null;
    price_date: string;
  } | null;
  analysts: Array<{
    name: string;
    firm: string | null;
    sector_focus: string | null;
  }>;
  documents: Array<{
    id: string;
    title: string | null;
    report_date: string | null;
    report_type: string | null;
    analyst_names: string[] | null;
  }>;
  estimates: Record<string, any>;
  valuation_methodology: string | null;
  topics: string[];
}

/**
 * Get company profile by ticker
 */
export const getCompanyProfile = async (ticker: string): Promise<CompanyProfile> => {
  const response = await fetch(`${API_ENDPOINTS.v3Documents.replace('/documents', '/company')}/${ticker.toUpperCase()}`, {
    headers: {
      'X-API-Key': API_KEY,
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Company profile failed: ${response.status}`);
  }

  return response.json();
};

// Prefetch cache for company/analyst profiles
const prefetchCache = new Map<string, { data: any; timestamp: number }>();
const PREFETCH_TTL = 60_000; // 1 minute

export const prefetchCompanyProfile = (ticker: string): void => {
  const key = `company:${ticker.toUpperCase()}`;
  const cached = prefetchCache.get(key);
  if (cached && Date.now() - cached.timestamp < PREFETCH_TTL) return;
  getCompanyProfile(ticker).then(data => {
    prefetchCache.set(key, { data, timestamp: Date.now() });
  }).catch(() => {});
};

export const prefetchAnalystProfile = (id: string): void => {
  const key = `analyst:${id}`;
  const cached = prefetchCache.get(key);
  if (cached && Date.now() - cached.timestamp < PREFETCH_TTL) return;
  getAnalystProfile(id).then(data => {
    prefetchCache.set(key, { data, timestamp: Date.now() });
  }).catch(() => {});
};

export const getCachedCompanyProfile = (ticker: string): CompanyProfile | null => {
  const cached = prefetchCache.get(`company:${ticker.toUpperCase()}`);
  if (cached && Date.now() - cached.timestamp < PREFETCH_TTL) return cached.data;
  return null;
};

export const getCachedAnalystProfile = (id: string): AnalystProfile | null => {
  const cached = prefetchCache.get(`analyst:${id}`);
  if (cached && Date.now() - cached.timestamp < PREFETCH_TTL) return cached.data;
  return null;
};

/**
 * Query the V3 RAG system
 * @param query - The user's question
 * @param conversationHistory - Previous messages for context (optional)
 * @param language - Language for response ('English' | 'Hebrew'), auto-detected if not provided
 */
export const v3Query = async (
  query: string,
  conversationHistory?: ConversationMessage[],
  language?: string
): Promise<V3QueryResponse> => {
  console.log('[v3Query] Sending query:', query, 'language:', language || 'auto', 'to:', API_ENDPOINTS.v3Query);

  try {
    const response = await fetch(API_ENDPOINTS.v3Query, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        query,
        verbose: false,
        log: true,
        conversation_history: conversationHistory,
        language: language || null  // Pass to backend, null means auto-detect
      }),
      credentials: 'omit',  // Don't send cookies for cross-origin
    });
    
    console.log('[v3Query] Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[v3Query] Error response:', errorText);
      throw new Error(`V3 query failed: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('[v3Query] Success, sources:', data.sources?.length || 0);
    return data;
  } catch (error) {
    console.error('[v3Query] Fetch error:', error);
    throw error;
  }
};

/**
 * Stream event types from V3 query stream
 */
export interface V3StreamEvent {
  type: 'status' | 'chunk' | 'sources' | 'done' | 'error' | 'corrected_answer';
  message?: string;  // For status events
  text?: string;     // For chunk events AND corrected_answer events
  data?: any;        // For sources events
  processing_time_ms?: number;  // For done events
  documents_used?: number;      // For done events
}

/**
 * Callbacks for streaming query
 */
export interface V3StreamCallbacks {
  onStatus?: (message: string) => void;
  onChunk?: (text: string, fullText: string) => void;
  onSources?: (sources: V3QueryResponse['sources']) => void;
  onDone?: (processingTimeMs: number, documentsUsed: number) => void;
  onError?: (error: string) => void;
  onCorrectedAnswer?: (text: string) => void;  // Replaces streamed answer with corrected citations
}

/**
 * Query the V3 RAG system with streaming response
 * @param query - The user's question
 * @param callbacks - Callbacks for handling stream events
 * @param language - Language for response ('English' | 'Hebrew'), auto-detected if not provided
 */
export const v3QueryStream = async (
  query: string,
  callbacks: V3StreamCallbacks,
  language?: string
): Promise<void> => {
  console.log('[v3QueryStream] Starting stream for:', query);
  let fullText = '';

  try {
    const response = await fetch(API_ENDPOINTS.v3QueryStream, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'Accept': 'text/event-stream',
        'X-API-Key': API_KEY,
      },
      body: JSON.stringify({
        query,
        language: language || null
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[v3QueryStream] Error response:', errorText);
      callbacks.onError?.(`Stream failed: ${response.status}`);
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      callbacks.onError?.('No response body');
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');

      // Keep the last incomplete chunk in buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const event: V3StreamEvent = JSON.parse(line.slice(6));

            switch (event.type) {
              case 'status':
                callbacks.onStatus?.(event.message || '');
                break;
              case 'chunk':
                fullText += event.text || '';
                callbacks.onChunk?.(event.text || '', fullText);
                break;
              case 'sources':
                callbacks.onSources?.(event.data || []);
                break;
              case 'corrected_answer':
                // Replace the full streamed text with corrected citations
                // This fixes [10] -> [3], etc. after grouping
                fullText = event.text || fullText;
                callbacks.onCorrectedAnswer?.(event.text || '');
                break;
              case 'done':
                callbacks.onDone?.(event.processing_time_ms || 0, event.documents_used || 0);
                break;
              case 'error':
                callbacks.onError?.(event.message || 'Unknown error');
                break;
            }
          } catch (e) {
            console.warn('[v3QueryStream] Failed to parse event:', line, e);
          }
        }
      }
    }
  } catch (error) {
    console.error('[v3QueryStream] Fetch error:', error);
    const msg = error instanceof Error ? error.message : 'Stream failed';
    const isSafariLoadFailed = msg.includes('Load failed') || msg.includes('Failed to fetch');
    if (isSafariLoadFailed && fullText) {
      callbacks.onChunk?.('\n\n---\n*Connection lost — showing partial answer.*', fullText + '\n\n---\n*Connection lost — showing partial answer.*');
      callbacks.onDone?.(0, 0);
    } else if (isSafariLoadFailed) {
      callbacks.onError?.('Connection lost. Safari pauses requests when you leave the app — please keep the screen on and try again.');
    } else {
      callbacks.onError?.(msg);
    }
  }
};

/**
 * Search V3 chunks (raw search without answer synthesis)
 */
export const v3Search = async (query: string, limit: number = 5): Promise<V3SearchResponse> => {
  const response = await fetch(`${API_ENDPOINTS.v3Search}?query=${encodeURIComponent(query)}&limit=${limit}`);
  
  if (!response.ok) {
    throw new Error(`V3 search failed: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Get all V3 documents
 */
export const v3GetDocuments = async (limit: number = 100): Promise<V3Document[]> => {
  const response = await fetch(`${API_ENDPOINTS.v3Documents}?limit=${limit}`);
  
  if (!response.ok) {
    throw new Error(`V3 documents failed: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Get V3 system stats
 */
export const v3GetStats = async (): Promise<V3Stats> => {
  const response = await fetch(API_ENDPOINTS.v3Stats);
  
  if (!response.ok) {
    throw new Error(`V3 stats failed: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Check V3 health
 */
export const v3HealthCheck = async (): Promise<{ status: string; database: string }> => {
  const response = await fetch(API_ENDPOINTS.v3Health);

  if (!response.ok) {
    throw new Error(`V3 health failed: ${response.status}`);
  }

  return response.json();
};


// ============================================
// SARAH - AGENTIC AI ASSISTANT
// ============================================

export interface SarahAgentCall {
  agent_name: string;
  input_summary: string;
  output_summary: string;
  duration_ms: number;
  success: boolean;
}

export interface SarahComparisonTable {
  headers: string[];
  rows: Array<Record<string, any>>;
}

export interface SarahSource {
  // Pipeline source format (from source_builder.py → group_sources_by_document → renumber_citations)
  id: string;              // "source-1"
  citation_num: number;    // 1
  citation?: string;       // "[1]"
  url?: string;            // Deep link URL
  content?: string;        // Block text excerpt
  similarity?: number;     // Semantic score
  document_id?: string;    // Document UUID
  ref_id?: number;         // Block reference ID
  page?: number;           // Page number
  block_type?: string;     // "text" | "table" | etc.
  breadcrumb?: string;     // "Section → Subsection"
  blocks_cited?: Array<{   // All blocks from this document
    ref_id: number;
    page?: number;
    block_type?: string;
    citation_num?: number;
    content_preview?: string;
  }>;
  ref_ids?: number[];      // All ref_ids for multi-block highlighting
  is_transcript?: boolean; // Whether this source is from a transcript
  speaker_name?: string;   // Speaker name for transcript sources
  start_time?: number;     // Audio timestamp in seconds
  para_id?: number;        // Paragraph ID for transcript deep-link
  metadata?: {
    block_id?: string;
    ref_id?: number;
    page?: number;
    title?: string;
    analysts?: string[];
    report_date?: string;
    all_refs?: number[];
    citation_nums?: number[];
    is_transcript?: boolean;
    speaker_name?: string;
    start_time?: number;
  };
}

export interface SarahResponse {
  answer: string;
  sources: SarahSource[];
  comparison_table?: SarahComparisonTable;
  agent_calls: SarahAgentCall[];
  total_duration_ms: number;
  confidence: number;
  suggested_follow_ups: string[];
  session_id?: string;
}

/**
 * Query Sarah - the Agentic AI Assistant
 *
 * Sarah orchestrates specialized sub-agents:
 * - IntentAgent: Understands what you want
 * - RouterAgent: Finds relevant documents
 * - NavigatorAgent: Locates relevant sections
 * - ExtractorAgent: Pulls specific data
 * - ComparatorAgent: Builds comparison tables
 *
 * @param query - The user's question
 * @param language - Language for response ('English' | 'Hebrew'), auto-detected if not provided
 */
export const querySarah = async (
  query: string,
  language?: string,
  sessionId?: string
): Promise<SarahResponse> => {
  console.log('[querySarah] Sending query:', query, 'language:', language || 'auto', 'session:', sessionId || 'new');

  try {
    const response = await fetch(API_ENDPOINTS.sarah, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        query,
        verbose: false,
        language: language || null,
        session_id: sessionId || null
      }),
    });

    console.log('[querySarah] Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[querySarah] Error response:', errorText);
      throw new Error(`Sarah query failed: ${response.status}`);
    }

    const data = await response.json();
    console.log('[querySarah] Success:', {
      confidence: data.confidence,
      sources: data.sources?.length || 0,
      agent_calls: data.agent_calls?.length || 0,
      duration_ms: data.total_duration_ms
    });
    return data;
  } catch (error) {
    console.error('[querySarah] Fetch error:', error);
    throw error;
  }
};

/**
 * Reasoning step from the pipeline (emitted as SSE "reasoning" events)
 */
export interface ReasoningStep {
  step: string;       // "research_context" | "documents_found" | "blocks_retrieved" | "evidence_analysis" | "themes_detected" | "research_plan" | "gateway" | "parallel_analysis"
  summary?: string;   // Human-readable description (most steps)
  message?: string;   // Free-form message (parallel_analysis, verification)
  documents?: Array<{ title: string; confidence: number; reason: string; sector?: string; topics?: string[] }>;  // For documents_found step
  // Research plan fields
  intent?: string;
  tickers?: string[];
  sectors?: string[];
  months_back?: number;
  synthesis_style?: string;
  reasoning?: string;
}

/**
 * Debug log entry from the pipeline (emitted as SSE "debug_log" events)
 */
export interface DebugLogEntry {
  phase: string;       // "understand" | "route" | "blocks" | "done"
  elapsed_ms?: number;
  [key: string]: any;  // phase-specific data
}

/**
 * Per-LLM-call cost breakdown
 */
export interface CostLLMCall {
  label: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
  cost: number;
}

/**
 * Structured cost data from the pipeline
 */
export interface CostData {
  llm_calls: CostLLMCall[];
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost: number;
  db_queries: number;
}

/**
 * Callbacks for Sarah streaming query
 */
export interface SarahStreamCallbacks {
  onStatus?: (message: string) => void;
  onChunk?: (text: string, fullText: string) => void;
  onSources?: (sources: SarahSource[]) => void;
  onSuggestions?: (suggestions: string[]) => void;
  onDone?: (processingTimeMs: number, documentsUsed: number, sessionId?: string, cost?: CostData) => void;
  onError?: (error: string) => void;
  onCorrectedAnswer?: (text: string) => void;
  onReasoning?: (step: ReasoningStep) => void;
  onDebugLog?: (entry: DebugLogEntry) => void;
}

/**
 * Query Sarah with streaming response (SSE)
 * Token-by-token answer generation with real-time status updates.
 */
export const querySarahStream = async (
  query: string,
  callbacks: SarahStreamCallbacks,
  language?: string,
  sessionId?: string
): Promise<void> => {
  console.log('[querySarahStream] Starting stream for:', query, 'session:', sessionId || 'new');
  let fullText = '';
  let receivedDone = false;

  try {
    const response = await fetch(API_ENDPOINTS.sarahStream, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'Accept': 'text/event-stream',
        'X-API-Key': API_KEY,
      },
      body: JSON.stringify({
        query,
        verbose: false,
        language: language || null,
        session_id: sessionId || null
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[querySarahStream] Error response:', errorText);
      callbacks.onError?.(`Sarah stream failed: ${response.status}`);
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      callbacks.onError?.('No response body');
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');

      // Keep the last incomplete chunk in buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const event = JSON.parse(line.slice(6));

            switch (event.type) {
              case 'status':
                callbacks.onStatus?.(event.message || '');
                break;
              case 'chunk':
                fullText += event.text || '';
                callbacks.onChunk?.(event.text || '', fullText);
                break;
              case 'sources':
                callbacks.onSources?.(event.data || []);
                break;
              case 'corrected_answer':
                fullText = event.text || fullText;
                callbacks.onCorrectedAnswer?.(event.text || '');
                break;
              case 'reasoning':
                if (event.data) {
                  callbacks.onReasoning?.(event.data as ReasoningStep);
                }
                break;
              case 'suggestions':
                callbacks.onSuggestions?.(event.data || []);
                break;
              case 'debug_log':
                callbacks.onDebugLog?.(event.data as DebugLogEntry);
                break;
              case 'done':
                receivedDone = true;
                callbacks.onDone?.(
                  event.processing_time_ms || 0,
                  event.documents_used || 0,
                  event.session_id,
                  event.cost
                );
                break;
              case 'error':
                callbacks.onError?.(event.message || 'Unknown error');
                break;
            }
          } catch (e) {
            console.warn('[querySarahStream] Failed to parse event:', line, e);
          }
        }
      }
    }

    // Stream ended without 'done' event — fire onDone with partial content
    if (!receivedDone && fullText) {
      console.warn('[querySarahStream] Stream ended without done event, finalizing with partial content');
      callbacks.onDone?.(0, 0);
    }
  } catch (error) {
    console.error('[querySarahStream] Fetch error:', error);
    const msg = error instanceof Error ? error.message : 'Sarah stream failed';
    // Safari mobile kills fetch when tab is backgrounded
    const isSafariLoadFailed = msg.includes('Load failed') || msg.includes('Failed to fetch');
    if (isSafariLoadFailed && fullText) {
      // We already have partial content — signal done with what we have
      callbacks.onChunk?.('\n\n---\n*Connection lost — showing partial answer.*', fullText + '\n\n---\n*Connection lost — showing partial answer.*');
      callbacks.onDone?.(0, 0);
    } else if (isSafariLoadFailed) {
      callbacks.onError?.('Connection lost. Safari pauses requests when you leave the app — please keep the screen on and try again.');
    } else {
      callbacks.onError?.(msg);
    }
  }
};

/**
 * Fetch recent server logs from the backend (journalctl)
 */
export const fetchServerLogs = async (lines: number = 300): Promise<string[]> => {
  try {
    const resp = await fetch(`${API_ENDPOINTS.serverLogs}?lines=${lines}`, {
      headers: { 'X-API-Key': API_KEY },
    });
    if (!resp.ok) return [`[error] HTTP ${resp.status}`];
    const data = await resp.json();
    return data.lines || [];
  } catch (e) {
    return [`[error] ${e instanceof Error ? e.message : 'fetch failed'}`];
  }
};
